<template>
  <div class="custom-numeric-interval">
    <Form
      ref="formRef"
      :rules="formRules"
      :model="state"
      :layout="ELayout.horizontal"
      autocomplete="off"
    >
      <FormItem
        v-if="state.isInterval"
        name="intervalValue"
        class="custom-numeric-interval__item"
      >
        <div
          class="custom-numeric-interval__interval-block"
          :class="{
            'custom-numeric-interval__interval-block_has-errors': hasErrors,
            'custom-numeric-interval__interval-block_with-addon-after': addonAfter,
          }"
        >
          <NumberInput
            ref="inputFromRef"
            class="custom-numeric-interval__item"
            isOnlyInteger
            :placeholder="placeholderFrom"
            :hasBorder="false"
            :value="state.valueFrom"
            @input="handleInputChange('valueFrom', $event)"
          />

          <div
            class="custom-numeric-interval__separator"
            @click="handleClickSeparator"
          >
            <NumericIntervalSeparatorSvg />
          </div>

          <NumberInput
            ref="inputFromRef"
            class="custom-numeric-interval__item custom-numeric-interval__item_second"
            :placeholder="placeholderTo"
            :value="state.valueTo"
            :hasBorder="false"
            isOnlyInteger
            @input="handleInputChange('valueTo', $event)"
          />
        </div>
      </FormItem>
      <FormItem
        v-else
        name="intervalValue"
        class="custom-numeric-interval__item"
      >
        <NumberInput
          ref="inputFromRef"
          class="custom-numeric-interval__item_from"
          :placeholder="placeholder"
          :value="state.valueFrom"
          isOnlyInteger
          @input="handleInputChange('valueFrom', $event)"
        />
      </FormItem>
    </Form>
    <Checkbox
      v-if="singleAndIntervalMode"
      class="custom-numeric-interval__checkbox"
      :checked="state.isInterval"
      @change="handleCheckboxChange($event)"
    >
      {{ tt('shared.interval') }}
    </Checkbox>
  </div>
</template>

<script lang="ts">

import {
  ComponentPublicInstance,
  defineComponent,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch,
  PropType,
  computed,
} from 'vue';
import {
  Form,
  FormItem,
} from 'ant-design-vue';

import {
  Checkbox,
  NumberInput,
} from '@/ui';
import NumericIntervalSeparatorSvg from '@/assets/svg/16x16/range.svg';
import { useFormHandlers } from '@/composables/useFormHandlers';
import { ELayout } from '@/constants';
import tt from '@/i18n/utils/translateText';

import { ENumericIntervalMode } from './constants';
import { TNumericIntervalError } from './types';

export default defineComponent({
  name: 'CustomNumericInterval',
  components: {
    NumberInput,
    Checkbox,
    Form,
    FormItem,
    NumericIntervalSeparatorSvg,
  },
  props: {
    initialValueFrom: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    initialValueTo: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    placeholderFrom: {
      type: String,
      default: '',
    },
    placeholderTo: {
      type: String,
      default: '',
    },
    addonAfter: {
      type: String,
      default: '',
    },
    precision: {
      type: Number,
      default: 2,
    },
    error: {
      type: [Object, null] as PropType<TNumericIntervalError | null>,
      default: null,
    },
    mode: {
      type: String as PropType<ENumericIntervalMode>,
      default: ENumericIntervalMode.singleAndInterval,
    },
    controlled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup(props, { emit, expose }) {
    const inputFromRef = ref<ComponentPublicInstance<HTMLInputElement>>();
    const formRef = ref<typeof Form | null>(null);

    const rules = { intervalValue: [] };

    const hasErrors = computed(() => props.error?.intervalValue);

    const {
      formRules,
      onControlChange,
      onError,
    } = useFormHandlers(formRef, { rules });

    const setError = () => {
      onError({ errors: props.error });
    };

    watch(() => props.error, setError);
    onMounted(setError);

    const singleAndIntervalMode = computed(() => props.mode === ENumericIntervalMode.singleAndInterval);

    const getInitialIsInterval = (mode: ENumericIntervalMode, from: number | null, to: number | null) => {
      if (mode === ENumericIntervalMode.single) return false;
      if (mode === ENumericIntervalMode.interval) return true;
      return from !== to;
    };

    const state = reactive({
      isInterval: getInitialIsInterval(props.mode, props.initialValueFrom, props.initialValueTo),
      valueFrom: props.initialValueFrom,
      valueTo: props.initialValueTo,
    });

    const setValueFromProps = () => {
      state.valueFrom = props.initialValueFrom;
      state.valueTo = props.initialValueTo;
    };

    watch(() => [props.initialValueFrom, props.initialValueTo], setValueFromProps);

    const onFocus = () => {
      nextTick().then(() => {
        inputFromRef.value?.focus();
      });
    };

    const onValidate = () => {
      formRef.value?.validate().catch(() => {});
    };

    expose({
      focus: onFocus,
      validate: onValidate,
    });

    const handleInputChange = (field: string, event: number) => {
      state[field] = event;
      onControlChange({ name: 'intervalValue' });
      emit('change', {
        from: state.valueFrom,
        to: state.isInterval ? state.valueTo : state.valueFrom,
      });
    };

    const handleCheckboxChange = (event: any) => {
      const isChecked: boolean = event.target.checked;
      state.isInterval = isChecked;
      state.valueTo = state.valueFrom;

      onFocus();

      emit('change', {
        from: state.valueFrom,
        to: state.isInterval ? state.valueTo : state.valueFrom,
      });
    };

    const handleClickSeparator = () => {
      onFocus();
    };

    return {
      state,
      inputFromRef,
      formRules,
      formRef,
      singleAndIntervalMode,
      ELayout,
      hasErrors,

      handleInputChange,
      handleCheckboxChange,
      handleClickSeparator,
      tt,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
